import React, { useCallback, useEffect, useState } from 'react';
import submissionWorkflowService, {
  Submission, SUBMISSION_STATUS_CONFIG,
} from '../../../services/submissionWorkflow.service';
import ProzessDetail from './ProzessDetail';
import { relativeZeit } from '../workflow/zeit';

/**
 * Alle Kundenprozesse eines Kandidaten (Regel 28). Mehrere gleichzeitige
 * Prozesse sind der Normalfall — sie laufen unabhängig voneinander.
 */

interface Props {
  candidateId: string;
  aktualisierungsZaehler?: number;
  onGeaendert?: () => void;
}

export const KundenprozesseListe: React.FC<Props> = ({
  candidateId, aktualisierungsZaehler = 0, onGeaendert,
}) => {
  const [prozesse, setProzesse] = useState<Submission[]>([]);
  const [laedt, setLaedt] = useState(true);
  const [fehler, setFehler] = useState<string | null>(null);
  const [offenerProzess, setOffenerProzess] = useState<string | null>(null);

  const laden = useCallback(async () => {
    try {
      setLaedt(true);
      setProzesse(await submissionWorkflowService.jeKandidat(candidateId));
      setFehler(null);
    } catch (e: any) {
      setFehler(e?.message || 'Die Kundenprozesse konnten nicht geladen werden.');
    } finally {
      setLaedt(false);
    }
  }, [candidateId]);

  useEffect(() => { laden(); }, [laden, aktualisierungsZaehler]);

  if (offenerProzess) {
    return (
      <ProzessDetail
        submissionId={offenerProzess}
        onSchliessen={() => { setOffenerProzess(null); laden(); }}
        onGeaendert={() => { laden(); onGeaendert?.(); }}
      />
    );
  }

  return (
    <div>
      {fehler && <div style={{ color: '#EF4444', fontSize: '0.82rem', marginBottom: '8px' }}>{fehler}</div>}

      {laedt && prozesse.length === 0 ? (
        <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>Wird geladen…</div>
      ) : prozesse.length === 0 ? (
        <div style={{ fontSize: '0.85rem', color: 'var(--text-tertiary)' }}>
          Noch kein Kundenprozess. Über „Bei Kunde vorstellen" lässt sich einer anlegen.
        </div>
      ) : (
        <div style={{ display: 'grid', gap: '7px' }}>
          {prozesse.map((p) => {
            const konfig = SUBMISSION_STATUS_CONFIG[p.process_status];
            const abgeschlossen = ['PLACED', 'REJECTED_CLIENT', 'REJECTED_CANDIDATE', 'WITHDRAWN', 'CLOSED']
              .includes(p.process_status);
            return (
              <div
                key={p.id}
                onClick={() => setOffenerProzess(p.id)}
                style={{
                  display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer',
                  padding: '9px 11px', borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--glass-border)', background: 'var(--glass)',
                  opacity: abgeschlossen ? 0.7 : 1,
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '0.87rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                    {p.client_company?.name || 'Kunde offen'}
                  </div>
                  <div style={{ fontSize: '0.77rem', color: 'var(--text-secondary)' }}>
                    {p.job_posting?.title || 'Ohne Stellenauftrag'}
                  </div>
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <div style={{
                    fontSize: '0.74rem', fontWeight: 700, color: konfig.color,
                    background: konfig.bgColor, borderRadius: '999px', padding: '2px 9px',
                    display: 'inline-block',
                  }}>
                    {konfig.label}
                  </div>
                  <div style={{ fontSize: '0.72rem', color: 'var(--text-tertiary)', marginTop: '2px' }}>
                    {relativeZeit(p.status_changed_at || p.created_at)}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default KundenprozesseListe;
